import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WaitlistSection from '../components/WaitlistSection';
import { Button } from '../components/ui/button';
import {
  ArrowRight, ChevronRight, Users, LayoutGrid, ClipboardList, Clock, ShieldCheck,
  FileText, MapPin, Tag, Lock, CheckCircle2, Building2, Handshake, Search,
  BadgeCheck, DollarSign, Repeat,
} from 'lucide-react';

// -----------------------------------------------------------------------------
// LandingPage — copy owned by product. Selling points:
// 1. Access to qualified opportunities
// 2. Multiple suppliers
// 3. Clear lead information
// Privacy / verification claims live only in the Trust & Protection section.
// -----------------------------------------------------------------------------

const VALUE_STATS = [
  { icon: LayoutGrid,    kicker: 'More choice',        value: 'Multiple lead suppliers' },
  { icon: FileText,      kicker: 'Clear information',  value: 'Lead Passports' },
  { icon: ClipboardList, kicker: 'Your requirements',  value: 'Your budget' },
  { icon: Clock,         kicker: '48 hours',           value: 'To raise a quality dispute' },
];

const BUYER_POINTS = [
  {
    icon: Users,
    title: 'More suppliers. More choice.',
    body: 'Access different lead sources through one account and find the suppliers that work for your business.',
  },
  {
    icon: FileText,
    title: "Know what you're buying.",
    body: 'Review the price, lead age, location, qualification details and available verification evidence before purchasing.',
  },
  {
    icon: ClipboardList,
    title: 'Set your requirements.',
    body: "Define what a suitable opportunity looks like, so sellers understand what you're willing to buy.",
  },
];

const SELLER_POINTS = [
  {
    icon: Search,
    title: 'Reach relevant buyers.',
    body: 'Put your leads in front of businesses seeking your categories and locations.',
  },
  {
    icon: DollarSign,
    title: 'Set your price.',
    body: 'Price your listings and agree terms with buyers through the marketplace.',
  },
  {
    icon: Repeat,
    title: 'Build repeat business.',
    body: 'Make your lead quality, source information and responsiveness reasons for buyers to return.',
  },
];

const HOW_STEPS = [
  { n: '1', title: 'Join as a buyer, seller or both.', body: 'Create your account and complete the required business verification.' },
  { n: '2', title: 'Find your match.',                  body: 'Buyers browse listings or post their requirements. Sellers list leads or respond to demand.' },
  { n: '3', title: 'Review the details.',               body: 'Check the Lead Passport, qualification information, price and applicable purchase terms.' },
  { n: '4', title: 'Trade through LeadXchange.',        body: 'Complete payment and lead delivery through the platform, with transaction records and dispute support.' },
];

const PASSPORT_POINTS = [
  { icon: Tag,         title: 'Source information',   body: 'Understand how the enquiry was generated.' },
  { icon: ClipboardList, title: 'Qualification details', body: "Check the prospect's stated needs against your requirements." },
  { icon: BadgeCheck,  title: 'Verification status',  body: 'See which checks have been completed and what evidence is available.' },
  { icon: Clock,       title: 'Lead history',         body: 'Review the available timestamps and delivery records.' },
];

const TRUST_ITEMS = [
  { icon: Building2,   title: 'Business verification',  body: 'Australian sellers must complete ABN verification before they can transact.' },
  { icon: Lock,        title: 'Controlled access',      body: 'Lead contact details stay restricted until purchase. Account and business information is protected through platform access controls.' },
  { icon: FileText,    title: 'Recorded transactions',  body: 'Offers, purchases, deliveries and disputes are recorded to support accountability.' },
  { icon: ShieldCheck, title: '48-hour dispute window', body: 'If a lead materially differs from its listing or fails a stated verification check, raise a dispute within 48 hours. Claims are reviewed under the Refund Policy.' },
  { icon: CheckCircle2,title: 'Secure payments',        body: 'Payments are processed through Stripe.' },
];

// Rotating demo listings — 3 variants so visitors see marketplace variety.
const DEMO_LEADS = [
  {
    category: 'Business Insurance',
    region: 'VIC',
    source: 'Comparison site',
    campaign: 'SME insurance enquiry',
    leadAge: 'Under 48 hours',
    priceAud: 320,
    chips: ['SMS verified', 'Consent captured', 'Source declared'],
  },
  {
    category: 'Commercial Solar',
    region: 'QLD',
    source: 'Industry directory',
    campaign: 'Warehouse rooftop enquiry',
    leadAge: 'Under 24 hours',
    priceAud: 285,
    chips: ['Site address captured', 'Decision-maker confirmed', 'Load profile shared'],
  },
  {
    category: 'Commercial Finance',
    region: 'NSW',
    source: 'Broker referral',
    campaign: 'Equipment finance enquiry',
    leadAge: 'Under 72 hours',
    priceAud: 410,
    chips: ['ABN verified', 'Turnover range shared', 'Purpose captured'],
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [demoIdx, setDemoIdx] = React.useState(0);

  React.useEffect(() => {
    const t = setInterval(() => setDemoIdx((i) => (i + 1) % DEMO_LEADS.length), 5000);
    return () => clearInterval(t);
  }, []);

  const demoLead = DEMO_LEADS[demoIdx];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-20 lg:pt-28 lg:pb-24">
          <div className="max-w-3xl animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-100 rounded-full mb-6">
              <span className="w-2 h-2 bg-[#0055FF] rounded-full" />
              <span className="text-xs font-mono font-medium text-blue-700 tracking-wider uppercase">
                Marketplace · Australia
              </span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-gray-900 leading-[1.05]">
              Better leads. More choice.
              <span className="block text-[#0055FF]">More opportunities to grow.</span>
            </h1>
            <p className="mt-6 text-lg text-gray-600 max-w-2xl leading-relaxed">
              Buy qualified business leads from multiple suppliers, or turn the leads you generate into revenue. Compare opportunities by requirements, price and available verification information — all in one marketplace.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                size="lg"
                onClick={() => navigate('/register?role=buyer')}
                className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-md h-12 px-6 text-base"
                data-testid="hero-find-leads-btn"
              >
                Find Leads
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/register?role=seller')}
                className="border-gray-300 text-gray-900 hover:bg-gray-50 rounded-md h-12 px-6 text-base"
                data-testid="hero-sell-leads-btn"
              >
                Sell Leads
              </Button>
              <button
                onClick={() => navigate('/marketplace')}
                className="text-sm text-[#0044CC] hover:text-[#0055FF] font-medium inline-flex items-center gap-1 ml-1"
                data-testid="hero-demo-link"
              >
                Explore the demo marketplace <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-gray-200 pt-8">
              {VALUE_STATS.map((s) => (
                <div key={s.kicker} className="flex gap-3">
                  <div className="w-8 h-8 rounded bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                    <s.icon className="w-4 h-4 text-[#0055FF]" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-gray-500 font-mono">{s.kicker}</div>
                    <div className="text-sm font-medium text-gray-900 mt-0.5">{s.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Marketplace preview */}
      <section className="py-20 bg-gray-50 border-y border-gray-200" data-testid="marketplace-preview-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-6">
              <div className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-mono font-medium mb-3">
                Marketplace preview
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900">
                See what your next opportunity could look like.
              </h2>
              <p className="mt-4 text-base text-gray-600 leading-relaxed">
                Explore example listings and see how lead pricing, qualification and verification information appear before you buy.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Button
                  onClick={() => navigate('/marketplace')}
                  className="bg-gray-900 hover:bg-gray-800 text-white rounded-md h-11 px-5"
                  data-testid="preview-browse-demo-btn"
                >
                  Browse Demo Marketplace
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <span className="text-xs text-gray-500">
                  Illustrative listings only. Sign in to access the live marketplace.
                </span>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm relative overflow-hidden" data-testid="demo-lead-card">
                <span className="absolute top-4 right-4 text-[10px] font-mono font-semibold uppercase tracking-wider px-2 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 rounded">
                  Example lead · Demo data
                </span>
                <div key={demoIdx} className="animate-fade-in-up">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-9 h-9 bg-blue-50 border border-blue-100 rounded flex items-center justify-center">
                      <Tag className="w-4 h-4 text-[#0055FF]" strokeWidth={2} />
                    </div>
                    <div>
                      <div className="font-heading text-base font-semibold text-gray-900" data-testid="demo-lead-category">{demoLead.category}</div>
                      <div className="text-xs text-gray-500">Example listing</div>
                    </div>
                  </div>

                  <dl className="grid grid-cols-2 gap-y-2.5 text-sm">
                    <dt className="text-gray-500">Region</dt>
                    <dd className="text-gray-900 font-medium text-right">{demoLead.region}</dd>
                    <dt className="text-gray-500">Source</dt>
                    <dd className="text-gray-900 font-medium text-right">{demoLead.source}</dd>
                    <dt className="text-gray-500">Campaign</dt>
                    <dd className="text-gray-900 font-medium text-right">{demoLead.campaign}</dd>
                    <dt className="text-gray-500">Lead age</dt>
                    <dd className="text-gray-900 font-medium text-right">{demoLead.leadAge}</dd>
                    <dt className="text-gray-500 pt-2 border-t border-gray-100">Price</dt>
                    <dd className="text-right pt-2 border-t border-gray-100">
                      <span className="font-mono text-base font-semibold text-gray-900">A${demoLead.priceAud}</span>
                    </dd>
                  </dl>

                  <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-x-3 gap-y-1.5 text-xs">
                    {demoLead.chips.map((c) => (
                      <span key={c} className="inline-flex items-center gap-1 text-[#0044CC] font-medium">
                        <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} /> {c}
                      </span>
                    ))}
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-500">
                    <Lock className="w-3 h-3" />
                    Full contact details become available after purchase in the live marketplace.
                  </div>
                </div>

                {/* Dots */}
                <div className="mt-5 flex items-center justify-center gap-2" data-testid="demo-lead-dots">
                  {DEMO_LEADS.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setDemoIdx(i)}
                      aria-label={`Show example ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all ${i === demoIdx ? 'w-6 bg-[#0055FF]' : 'w-1.5 bg-gray-300 hover:bg-gray-400'}`}
                      data-testid={`demo-lead-dot-${i}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Buyers */}
      <section className="py-24" data-testid="for-buyers-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-mono font-medium mb-3">
              For buyers
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900">
              Find leads that fit your business.
            </h2>
            <p className="mt-4 text-base text-gray-600 leading-relaxed">
              Tell us what you're looking for: your services, locations, qualification requirements and budget. Explore available listings or post your demand to connect with relevant suppliers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {BUYER_POINTS.map((p, i) => (
              <PointCard key={p.title} point={p} testid={`buyer-point-${i}`} />
            ))}
          </div>

          <div className="mt-10">
            <Button
              onClick={() => navigate('/register?role=buyer')}
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-md h-11 px-5"
              data-testid="cta-buyer-section-btn"
            >
              Get Started as a Buyer
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* For Sellers */}
      <section className="py-24 bg-gray-50 border-y border-gray-200" data-testid="for-sellers-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-mono font-medium mb-3">
              For sellers
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900">
              Turn qualified leads into revenue.
            </h2>
            <p className="mt-4 text-base text-gray-600 leading-relaxed">
              Connect with businesses looking for the opportunities you generate. List available leads or respond to buyer demand with an offer that matches their requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {SELLER_POINTS.map((p, i) => (
              <PointCard key={p.title} point={p} testid={`seller-point-${i}`} />
            ))}
          </div>

          <div className="mt-10">
            <Button
              onClick={() => navigate('/register?role=seller')}
              className="bg-gray-900 hover:bg-gray-800 text-white rounded-md h-11 px-5"
              data-testid="cta-seller-section-btn"
            >
              Get Started as a Seller
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-mono font-medium mb-3">
              How it works
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900">
              From lead to opportunity.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {HOW_STEPS.map((s, i) => (
              <div key={s.n} className="border border-gray-200 rounded-lg p-6 bg-white" data-testid={`how-step-${i}`}>
                <div className="font-mono text-xs font-semibold text-[#0055FF] mb-3">STEP {s.n}</div>
                <h3 className="font-heading text-base font-semibold text-gray-900">{s.title}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Passport */}
      <section className="py-24 bg-gray-900 text-white" data-testid="lead-passport-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-blue-300 font-mono font-medium mb-3">
              Lead Passport
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-white">
              Clearer information. Better buying decisions.
            </h2>
            <p className="mt-4 text-base text-gray-300 leading-relaxed">
              A Lead Passport brings the listing's source, age, qualification details, consent records and verification results together, so you can assess the opportunity before buying.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PASSPORT_POINTS.map((p, i) => (
              <div key={p.title} className="bg-gray-800/60 border border-white/10 rounded-lg p-5" data-testid={`passport-point-${i}`}>
                <div className="w-9 h-9 bg-blue-500/15 border border-blue-400/20 rounded flex items-center justify-center mb-3">
                  <p.icon className="w-4 h-4 text-blue-300" strokeWidth={2} />
                </div>
                <div className="font-heading text-base font-semibold text-white">{p.title}</div>
                <div className="mt-2 text-sm text-gray-300 leading-relaxed">{p.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Protection */}
      <section className="py-24" data-testid="trust-protection-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-mono font-medium mb-3">
              Trust &amp; protection
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900">
              Trade with confidence.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {TRUST_ITEMS.map((t, i) => (
              <div key={t.title} className="border border-gray-200 rounded-lg p-5 bg-white" data-testid={`trust-item-${i}`}>
                <div className="w-9 h-9 bg-blue-50 border border-blue-100 rounded flex items-center justify-center mb-3">
                  <t.icon className="w-4 h-4 text-[#0055FF]" strokeWidth={2} />
                </div>
                <div className="font-heading text-base font-semibold text-gray-900">{t.title}</div>
                <div className="mt-2 text-sm text-gray-600 leading-relaxed">{t.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Early Access — waitlist */}
      <WaitlistSection />

      {/* Final CTA */}
      <section className="bg-gray-900 py-20" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-white">
            Your next customer. Your next buyer. One marketplace.
          </h2>
          <p className="mt-4 text-base text-gray-300">
            Find opportunities for your business — or buyers for the leads you generate.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/register?role=buyer')}
              className="bg-white hover:bg-gray-100 text-gray-900 rounded-md h-12 px-6"
              data-testid="cta-buyer-btn"
            >
              I Want to Buy Leads
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
            <Button
              size="lg"
              onClick={() => navigate('/register?role=seller')}
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-md h-12 px-6"
              data-testid="cta-seller-btn"
            >
              I Want to Sell Leads
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const PointCard = ({ point, testid }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid={testid}>
    <div className="w-9 h-9 bg-blue-50 border border-blue-100 rounded flex items-center justify-center mb-3">
      <point.icon className="w-4 h-4 text-[#0055FF]" strokeWidth={2} />
    </div>
    <h3 className="font-heading text-base font-semibold text-gray-900">{point.title}</h3>
    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{point.body}</p>
  </div>
);
